import React from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Toolbar,
  Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import UTrustLogo from '../../assets/appLogos/UTrust.png';

const primaryColor = '#0A7F74';

type ActiveItem = 'home' | 'features' | 'contact';

export const PublicLandingNav: React.FC<{ active: ActiveItem }> = () => {
  const navigate = useNavigate();

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid #eef2f1'
      }}
    >
      <Container maxWidth="lg" sx={{ px: '1px !important' }}>
        <Toolbar disableGutters sx={{ justifyContent: 'space-between', minHeight: { xs: 58, md: 64 } }}>
          <Box
            component="img"
            src={UTrustLogo}
            sx={{ height: 130, width: 130, objectFit: 'contain', cursor: 'pointer' }}
            onClick={() => navigate('/')}
          />

          {/* <Stack direction="row" spacing={3} sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Typography
              sx={{
                color: active === 'home' ? primaryColor : '#374151',
                fontWeight: active === 'home' ? 700 : 500,
                cursor: 'pointer'
              }}
              onClick={() => navigate('/')}
            >
              Home
            </Typography>
            <Typography
              sx={{
                color: active === 'contact' ? primaryColor : '#374151',
                fontWeight: active === 'contact' ? 700 : 500,
                cursor: 'pointer'
              }}
              onClick={() => navigate('/contact')}
            >
              Contact
            </Typography>
          </Stack> */}

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              variant="contained"
              onClick={() => navigate('/contact')}
              sx={{
                bgcolor: '#dc2626',
                borderRadius: '10px',
                textTransform: 'none',
                px: { xs: 1.5, md: 2.25 },
                fontSize: { xs: '0.75rem', md: '0.95rem' },
                fontWeight: 700,
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: '#b91c1c' }
              }}
            >
              Contact Us
            </Button>
            <Button
              onClick={() => navigate('/login?tab=signin')}
              startIcon={<AccountCircleIcon sx={{ color: '#111827' }} />}
              sx={{ color: '#111827', textTransform: 'none', fontWeight: 600 }}
            >
              Log In
            </Button>
          </Stack>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export const PublicLandingFooter: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #0a0a0a 0%, #161616 100%)',
        pt: { xs: 4, md: 5 },
        pb: 2.5,
        color: 'white'
      }}
    >
      <Container maxWidth="xl" sx={{ px: '16px !important' }}>
        <Grid container spacing={{ xs: 3, md: 5 }} alignItems="flex-start">
          <Grid item xs={12} md={3}>
            <Box
              component="img"
              src={UTrustLogo}
              sx={{ width: 145, height: 'auto', mb: 1 }}
            />
            <Typography sx={{ opacity: 0.9, maxWidth: 260, fontSize: '0.92rem', lineHeight: 1.5 }}>
              Secure. Collaborative. Transparent.
              <br />
              Building trust in every record.
            </Typography>
            <Stack direction="row" spacing={0.8} sx={{ mt: 2 }}>
              {[FacebookIcon, TwitterIcon, LinkedInIcon, InstagramIcon, YouTubeIcon].map((Icon, i) => {
                const isInsta = i === 3;
                const btn = (
                  <IconButton
                    key={i}
                    size="small"
                    sx={{
                      color: 'white',
                      bgcolor: 'rgba(255,255,255,0.12)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                    }}
                  >
                    <Icon sx={{ fontSize: 18 }} />
                  </IconButton>
                );
                return isInsta ? (
                  <a
                    key={i}
                    href="https://www.instagram.com/utrust415?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw=="
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'flex' }}
                  >
                    {btn}
                  </a>
                ) : btn;
              })}
            </Stack>
          </Grid>

          <Grid item xs={6} sm={4} md={1.8}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Product</Typography>
            <Typography sx={{ opacity: 0.9, fontSize: '0.95rem', mb: 0.4 }}>Features</Typography>
            <Typography sx={{ opacity: 0.9, fontSize: '0.95rem', mb: 0.4 }}>Security</Typography>
            <Typography sx={{ opacity: 0.9, fontSize: '0.95rem' }}>Integrations</Typography>
          </Grid>

          <Grid item xs={6} sm={4} md={1.8}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Company</Typography>
            <Typography sx={{ opacity: 0.9, fontSize: '0.95rem', mb: 0.4 }}>Careers</Typography>
            <Typography sx={{ opacity: 0.9, fontSize: '0.95rem' }}>Contact Us</Typography>
          </Grid>

          <Grid item xs={6} sm={4} md={1.8}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Support</Typography>
            <Typography sx={{ opacity: 0.9, fontSize: '0.95rem', mb: 0.4 }}>Help Center</Typography>
            <Typography
              onClick={() => navigate('/terms-and-conditions')}
              sx={{ opacity: 0.9, fontSize: '0.95rem', mb: 0.4, cursor: 'pointer', '&:hover': { opacity: 1, textDecoration: 'underline' } }}
            >Terms of Service</Typography>
            <Typography
              onClick={() => navigate('/privacy-policy')}
              sx={{ opacity: 0.9, fontSize: '0.95rem', mb: 0.4, cursor: 'pointer', '&:hover': { opacity: 1, textDecoration: 'underline' } }}
            >Privacy Policy</Typography>
            <Typography sx={{ opacity: 0.9, fontSize: '0.95rem' }}>Status</Typography>
          </Grid>

          <Grid item xs={12} md={3.6}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Stay updated</Typography>
            <Typography sx={{ opacity: 0.9, fontSize: '0.95rem', mb: 1.5 }}>
              Subscribe to get news and updates.
            </Typography>
            <Stack direction="row" spacing={0}>
              <TextField
                size="small"
                placeholder="Enter your email"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    borderRadius: '8px 0 0 8px',
                    '& fieldset': { borderColor: 'transparent' }
                  }
                }}
              />
              <Button
                variant="contained"
                sx={{
                  bgcolor: '#000000',
                  borderRadius: '0 8px 8px 0',
                  textTransform: 'none',
                  px: 2.5,
                  fontWeight: 700,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#111111' }
                }}
              >
                Subscribe
              </Button>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.5, borderColor: 'rgba(255,255,255,0.18)' }} />
        <Typography variant="body2" sx={{ textAlign: 'center', opacity: 0.85 }}>
          © 2024 UTrust Platforms. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
};
